import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

const JOB_SELECT = "*, partner_orgs(name, industry), crawler_metadata(source_url, source_name, company_name, external_apply_url)";

export function useSavedJobs() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch just the IDs (lightweight, used for bookmark state)
  const { data: savedJobIds = [] } = useQuery({
    queryKey: ["saved_job_ids", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saved_jobs")
        .select("job_id")
        .eq("user_id", user!.id);
      if (error) throw error;
      return data.map((r: any) => r.job_id as string);
    },
    staleTime: 30_000,
  });

  // Fetch full job details for the saved jobs page
  const { data: savedJobs = [], isLoading: isLoadingSaved } = useQuery({
    queryKey: ["saved_jobs_detail", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saved_jobs")
        .select(`job_id, created_at, job_posts(${JOB_SELECT})`)
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data
        .map((r: any) => r.job_posts)
        .filter(Boolean);
    },
  });

  const toggleSave = useMutation({
    mutationFn: async (jobPostId: string) => {
      if (!user) throw new Error("Você precisa estar logado para salvar vagas.");
      const alreadySaved = savedJobIds.includes(jobPostId);
      if (alreadySaved) {
        const { error } = await supabase
          .from("saved_jobs")
          .delete()
          .eq("user_id", user.id)
          .eq("job_id", jobPostId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("saved_jobs")
          .insert({ user_id: user.id, job_id: jobPostId });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved_job_ids", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["saved_jobs_detail", user?.id] });
    },
  });

  const isSaved = (jobPostId: string) => savedJobIds.includes(jobPostId);

  return { savedJobIds, savedJobs, isLoadingSaved, toggleSave, isSaved, user };
}
